import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
// Self-hosted fonts (bundled from npm, served from our own origin — no
// third-party browser calls). Inter 400–800.
//
// v0.6.12 — the four JetBrains Mono weights that used to be imported here are
// gone. Nothing applies that face any more (see index.css), so importing it
// only cost the operator four font files on every page load.
import "@fontsource/inter/400.css";
import "@fontsource/inter/500.css";
import "@fontsource/inter/600.css";
import "@fontsource/inter/700.css";
import "@fontsource/inter/800.css";
import "@/index.css";
import "@/i18n";  // v0.4.5 — init i18next before App renders
import App from "@/App";

// CRA dev-only: silence the full-screen red error overlay. It was firing
// for benign third-party noise (sanitized "Script error." from PostHog's
// crossorigin analytics bundle) and for handled promise rejections
// (e.g. a 409 SIGNOFF_REQUIRED on export), covering the whole app.
//
// Two listeners on the WINDOW in CAPTURE phase, registered before the
// React tree mounts so they fire before react-dev-overlay's bubble-phase
// listeners. `stopImmediatePropagation()` is the only thing that
// actually prevents the overlay from showing in CRA 5's overlay impl.
if (process.env.NODE_ENV !== "production") {
  const isThirdPartyNoise = (msg) =>
    typeof msg === "string" &&
    (msg === "Script error." || msg === "Script error" || msg.startsWith("ResizeObserver"));

  window.addEventListener(
    "error",
    (e) => {
      if (isThirdPartyNoise(e.message)) {
        // eslint-disable-next-line no-console
        console.warn("[suppressed third-party error]", e.message, e.filename);
        e.stopImmediatePropagation();
        e.preventDefault();
      }
    },
    true, // capture
  );

  window.addEventListener(
    "unhandledrejection",
    (e) => {
      // eslint-disable-next-line no-console
      console.warn("[unhandledrejection]", e.reason);
      e.stopImmediatePropagation();
      e.preventDefault();
    },
    true, // capture
  );
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </React.StrictMode>,
);
